// src/pages/Admin_Menu_Edit.jsx

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";

import {
  ArrowLeft,
  UserCog,
  Building2,
  Mail,
  Phone,
  MapPin,
  ShieldCheck,
  Save,
  Image as ImageIcon,
  Users as UsersIcon,
  Boxes,
  KeyRound,
  BellRing,
  Siren,
  Plus,
  Trash2,
  PhoneCall,
  MessageSquareText,
} from "lucide-react";

const API = "https://superadmin.nissiindia.com";
const MEDIA_BASE = API;

const MAX_PANIC_RECIPIENTS = 25;

const DEFAULT_PANIC_VOICE_TEMPLATE =
  "Emergency alert. Guard {id}, {name}, has triggered a panic alert.";

/* =========================================================
   MEDIA HELPERS
========================================================= */

function toAbsMediaUrl(pathValue) {
  if (!pathValue) {
    return "";
  }

  if (/^https?:\/\//i.test(pathValue)) {
    return pathValue;
  }

  if (pathValue.startsWith("/")) {
    return `${MEDIA_BASE}${pathValue}`;
  }

  return `${MEDIA_BASE}/${pathValue}`;
}

/* =========================================================
   NUMBER HELPERS
========================================================= */

/**
 * Converts:
 *
 * +91 98765 43210
 * +91-98765-43210
 * +91 (98765) 43210
 *
 * Into:
 *
 * +919876543210
 */
function cleanInternationalNumber(value) {
  return String(value || "")
    .trim()
    .replace(/[\s()-]/g, "");
}

/**
 * Valid E.164 examples:
 *
 * +14155552671
 * +919876543210
 */
function isValidE164Number(value) {
  const cleaned = cleanInternationalNumber(value);

  return /^\+[1-9]\d{7,14}$/.test(cleaned);
}

/* =========================================================
   TENANT ID
========================================================= */

function useTenantId() {
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);

  const tenantIdFromQuery = queryParams.get("tid");

  const tenantIdFromLocalStorage = useMemo(() => {
    try {
      const raw = localStorage.getItem("nissi_admin");

      if (!raw) {
        return null;
      }

      const adminData = JSON.parse(raw);

      return adminData?.tenantId || adminData?.TenantId || null;
    } catch (error) {
      console.error("[ADMIN EDIT] Unable to read tenant ID:", error);

      return null;
    }
  }, []);

  return tenantIdFromQuery || tenantIdFromLocalStorage || 1;
}

/* =========================================================
   COMPONENT
========================================================= */

export default function Admin_Menu_Edit() {
  const { username: routeUsername } = useParams();

  const tenantId = useTenantId();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [username] = useState(decodeURIComponent(routeUsername || ""));

  const [form, setForm] = useState({
    tenant_code: "",
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    num_users: 5,
    num_licenses: 0,
    num_clients: 0,
    user_map: "google",
    payroll_enabled: 0,
    notification_source: "both",
    is_active: 1,
    logo_path: "",
  });

  const [panicFromNumber, setPanicFromNumber] = useState("");
  const [panicRecipients, setPanicRecipients] = useState([""]);
  const [panicEnabled, setPanicEnabled] = useState(false);

  const [
    panicVoiceMessageTemplate,
    setPanicVoiceMessageTemplate,
  ] = useState(DEFAULT_PANIC_VOICE_TEMPLATE);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  /* =========================================================
     LOAD ADMIN DETAILS
  ========================================================= */

  useEffect(() => {
    let isMounted = true;

    async function loadAdminDetails() {
      if (!tenantId || !username) {
        return;
      }

      setLoading(true);

      try {
        const response = await axios.get(
          `${API}/api/mysql/tenant/${tenantId}/admins/${encodeURIComponent(
            username
          )}`
        );

        const row = response.data?.row;

        const panicSettings = response.data?.panicSettings || {};

        if (!row) {
          throw new Error("Admin licensing record not found");
        }

        if (!isMounted) {
          return;
        }

        setForm({
          tenant_code: row.tenant_code || "",
          company_name: row.company_name || "",
          contact_name: row.contact_name || "",
          email: row.email || "",
          phone: row.phone || "",
          address: row.address || "",
          num_users: row.num_users ?? 5,
          num_licenses: row.num_licenses ?? 0,
          num_clients: row.num_clients ?? 0,
          user_map: row.user_map || "google",
          payroll_enabled: row.payroll_enabled ? 1 : 0,

          notification_source: ["whatsapp", "mail", "both"].includes(
            String(row.notification_source || "").toLowerCase()
          )
            ? String(row.notification_source).toLowerCase()
            : "both",

          is_active: row.is_active ? 1 : 0,
          logo_path: row.logo_path || "",
        });

        setLogoPreview(toAbsMediaUrl(row.logo_path || ""));

        setPanicFromNumber(panicSettings.fromNumber || "");

        const loadedRecipients = Array.isArray(panicSettings.recipientNumbers)
          ? panicSettings.recipientNumbers
              .map((number) => String(number || "").trim())
              .filter(Boolean)
          : [];

        setPanicRecipients(loadedRecipients.length ? loadedRecipients : [""]);

        setPanicVoiceMessageTemplate(
          panicSettings.voiceMessageTemplate || DEFAULT_PANIC_VOICE_TEMPLATE
        );

        setPanicEnabled(Boolean(panicSettings.enabled));
      } catch (error) {
        console.error("[ADMIN EDIT] Load error:", error);

        if (!isMounted) {
          return;
        }

        Swal.fire(
          "Error",
          error?.response?.data?.error ||
            error.message ||
            "Unable to load admin details",
          "error"
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadAdminDetails();

    return () => {
      isMounted = false;
    };
  }, [tenantId, username]);

  /* =========================================================
     VALIDATION HELPERS
  ========================================================= */

  const isEmail = (value) => {
    return !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const isPhone = (value) => {
    return !value || String(value).length <= 50;
  };

  /* =========================================================
     GENERAL FIELD HANDLERS
  ========================================================= */

  const onChange = (event) => {
    const { name, value } = event.target;

    setForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const onChangeUsers = (event) => {
    const parsedValue = Number(event.target.value || 1);

    const value = Math.max(1, Number.isFinite(parsedValue) ? parsedValue : 1);

    setForm((currentForm) => ({
      ...currentForm,
      num_users: value,
    }));
  };

  const onChangeLicenses = (event) => {
    const parsedValue = Number(event.target.value || 0);

    const value = Math.max(0, Number.isFinite(parsedValue) ? parsedValue : 0);

    setForm((currentForm) => ({
      ...currentForm,
      num_licenses: value,
    }));
  };

  const onChangeClients = (event) => {
    const parsedValue = Number(event.target.value || 0);

    const value = Math.max(0, Number.isFinite(parsedValue) ? parsedValue : 0);

    setForm((currentForm) => ({
      ...currentForm,
      num_clients: value,
    }));
  };

  const onChangePayrollEnabled = (event) => {
    setForm((currentForm) => ({
      ...currentForm,
      payroll_enabled: event.target.value === "1" ? 1 : 0,
    }));
  };

  const onToggleActive = () => {
    setForm((currentForm) => ({
      ...currentForm,
      is_active: currentForm.is_active ? 0 : 1,
    }));
  };

  /* =========================================================
     PANIC SETTINGS HANDLERS
  ========================================================= */

  const onChangePanicFromNumber = (event) => {
    setPanicFromNumber(event.target.value);
  };

  const onChangePanicRecipient = (index, value) => {
    setPanicRecipients((currentRecipients) =>
      currentRecipients.map((recipient, recipientIndex) =>
        recipientIndex === index ? value : recipient
      )
    );
  };

  const addPanicRecipient = () => {
    if (panicRecipients.length >= MAX_PANIC_RECIPIENTS) {
      Swal.fire(
        "Maximum Reached",
        `Only ${MAX_PANIC_RECIPIENTS} panic recipient numbers are allowed.`,
        "warning"
      );

      return;
    }

    setPanicRecipients((currentRecipients) => [...currentRecipients, ""]);
  };

  const removePanicRecipient = (index) => {
    setPanicRecipients((currentRecipients) => {
      const updated = currentRecipients.filter(
        (_recipient, recipientIndex) => recipientIndex !== index
      );

      return updated.length ? updated : [""];
    });
  };

  const onTogglePanicEnabled = () => {
    setPanicEnabled((currentValue) => !currentValue);
  };

  const onChangePanicVoiceMessageTemplate = (event) => {
    setPanicVoiceMessageTemplate(event.target.value);
  };

  /* =========================================================
     LOGO
  ========================================================= */

  const onLogoFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;

    if (!selectedFile) {
      setLogoFile(null);
      return;
    }

    const allowedTypes = new Set([
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/webp",
    ]);

    if (!allowedTypes.has(selectedFile.type)) {
      Swal.fire(
        "Invalid File",
        "Only PNG, JPG, JPEG and WEBP files are allowed.",
        "warning"
      );

      event.target.value = "";
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      Swal.fire(
        "File Too Large",
        "Logo file cannot be larger than 2MB.",
        "warning"
      );

      event.target.value = "";
      return;
    }

    setLogoFile(selectedFile);

    const previewUrl = URL.createObjectURL(selectedFile);

    setLogoPreview((currentPreview) => {
      if (currentPreview && currentPreview.startsWith("blob:")) {
        URL.revokeObjectURL(currentPreview);
      }

      return previewUrl;
    });
  };

  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  async function uploadLogo() {
    if (!logoFile) {
      return null;
    }

    const formData = new FormData();

    formData.append("tenantId", String(tenantId));
    formData.append("username", username);
    formData.append("file", logoFile);

    const response = await axios.post(
      `${API}/api/tenant/mysql/logo`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    return response.data?.logoPath || null;
  }

  /* =========================================================
     SUBMIT
  ========================================================= */

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!form.company_name?.trim()) {
      Swal.fire("Validation", "Company name is required.", "warning");
      return;
    }

    if (!form.contact_name?.trim()) {
      Swal.fire("Validation", "Contact name is required.", "warning");
      return;
    }

    if (!isEmail(form.email)) {
      Swal.fire(
        "Invalid Email",
        "Please enter a valid email address.",
        "warning"
      );
      return;
    }

    if (!isPhone(form.phone)) {
      Swal.fire(
        "Invalid Phone",
        "Phone must be 50 characters or fewer.",
        "warning"
      );
      return;
    }

    if (!(Number(form.num_users) >= 1)) {
      Swal.fire(
        "Invalid Users",
        "Users must be a positive number.",
        "warning"
      );
      return;
    }

    if (Number(form.num_licenses) < 0) {
      Swal.fire(
        "Invalid License Limit",
        "License limit cannot be negative.",
        "warning"
      );
      return;
    }

    if (Number(form.num_clients) < 0) {
      Swal.fire(
        "Invalid Client Limit",
        "Client limit cannot be negative.",
        "warning"
      );
      return;
    }

    const cleanedFromNumber = cleanInternationalNumber(panicFromNumber);

    const cleanedRecipients = panicRecipients
      .map((number) => cleanInternationalNumber(number))
      .filter(Boolean);

    if (cleanedFromNumber && !isValidE164Number(cleanedFromNumber)) {
      Swal.fire(
        "Invalid Plivo Number",
        "Plivo From Number must use international E.164 format, for example +14155552671.",
        "warning"
      );
      return;
    }

    for (const recipientNumber of cleanedRecipients) {
      if (!isValidE164Number(recipientNumber)) {
        Swal.fire(
          "Invalid Recipient Number",
          `${recipientNumber} is invalid. Use E.164 format, for example +919876543210.`,
          "warning"
        );
        return;
      }
    }

    const uniqueRecipients = new Set(cleanedRecipients);

    if (uniqueRecipients.size !== cleanedRecipients.length) {
      Swal.fire(
        "Duplicate Numbers",
        "The same panic recipient number cannot be entered more than once.",
        "warning"
      );
      return;
    }

    if (panicEnabled && !cleanedFromNumber) {
      Swal.fire(
        "Missing From Number",
        "Plivo From Number is required when panic calling is enabled.",
        "warning"
      );
      return;
    }

    if (panicEnabled && cleanedRecipients.length === 0) {
      Swal.fire(
        "Missing Recipients",
        "Add at least one recipient number when panic calling is enabled.",
        "warning"
      );
      return;
    }

    if (
      panicVoiceMessageTemplate &&
      panicVoiceMessageTemplate.trim().length > 500
    ) {
      Swal.fire(
        "Invalid Voice Message",
        "Panic call voice message must be 500 characters or fewer.",
        "warning"
      );
      return;
    }

    setSaving(true);

    try {
      let uploadedLogo = null;

      if (logoFile) {
        uploadedLogo = await uploadLogo();

        if (uploadedLogo) {
          setForm((currentForm) => ({
            ...currentForm,
            logo_path: uploadedLogo,
          }));

          setLogoPreview(toAbsMediaUrl(uploadedLogo));
        }
      }

      const payload = {
        company_name: form.company_name?.trim() || null,
        contact_name: form.contact_name?.trim() || null,
        email: form.email?.trim() || null,
        phone: form.phone?.trim() || null,
        address: form.address?.trim() || null,
        num_users: Number(form.num_users),
        num_licenses: Number(form.num_licenses),
        num_clients: Number(form.num_clients),
        user_map: form.user_map || "google",
        payroll_enabled: form.payroll_enabled ? 1 : 0,
        notification_source: form.notification_source || "both",
        is_active: form.is_active ? 1 : 0,

        panicSettings: {
          fromNumber: cleanedFromNumber || null,
          recipientNumbers: cleanedRecipients,
          voiceMessageTemplate: panicVoiceMessageTemplate.trim() || null,
          enabled: panicEnabled,
        },
      };

      const response = await axios.patch(
        `${API}/api/mysql/tenant/${tenantId}/admins/${encodeURIComponent(
          username
        )}`,
        payload
      );

      const savedPanicSettings = response.data?.panicSettings;

      if (savedPanicSettings) {
        setPanicFromNumber(savedPanicSettings.fromNumber || "");

        setPanicRecipients(
          Array.isArray(savedPanicSettings.recipientNumbers) &&
            savedPanicSettings.recipientNumbers.length
            ? savedPanicSettings.recipientNumbers
            : [""]
        );

        setPanicVoiceMessageTemplate(
          savedPanicSettings.voiceMessageTemplate ||
            DEFAULT_PANIC_VOICE_TEMPLATE
        );

        setPanicEnabled(Boolean(savedPanicSettings.enabled));
      }

      await Swal.fire(
        "Saved",
        "Admin and panic settings updated successfully.",
        "success"
      );

      navigate("/superAdmin/admin_Menu");
    } catch (error) {
      console.error("[ADMIN EDIT] Save error:", error);

      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          error.message ||
          "Unable to update admin",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const statusPill = form.is_active
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
    : "bg-rose-50 text-rose-700 ring-1 ring-rose-200";

  const panicStatusPill = panicEnabled
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
    : "bg-slate-100 text-slate-600 ring-1 ring-slate-200";

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="min-h-screen bg-[radial-gradient(1100px_500px_at_50%_-120px,rgba(15,23,42,0.10),transparent)] bg-slate-100">
      <div className="flex flex-col md:h-screen md:flex-row md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:h-full md:w-3/4 md:overflow-auto">
          <div className="px-3 pt-5 sm:px-4 lg:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
                  <UserCog className="h-5 w-5" />
                </div>

                <div>
                  <h1 className="text-2xl font-semibold text-slate-900">
                    Edit Admin — <span className="text-slate-700">{username}</span>
                  </h1>

                  <p className="text-sm text-slate-600">
                    <span className="inline-flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-500" />
                      Tenant ID: {tenantId}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Link
                  to="/superAdmin/admin_Menu"
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-1.5 text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to List
                </Link>

                <div className="rounded-xl bg-white px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-200">
                  <Time />
                </div>
              </div>
            </div>
          </div>

          <div className="px-3 py-6 sm:px-4 lg:px-6">
            <div className="grid grid-cols-12 gap-4 lg:gap-6">
              <div className="col-span-12 rounded-2xl border border-slate-200/70 bg-white/80 shadow-xl backdrop-blur xl:col-span-8">
                <div className="flex items-center gap-2 rounded-t-2xl border-b border-slate-200/70 bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3">
                  <ShieldCheck className="h-4 w-4 text-indigo-600" />

                  <h3 className="text-sm font-semibold text-slate-800">
                    Admin Details
                  </h3>
                </div>

                {loading ? (
                  <div className="p-6 text-slate-600">Loading details…</div>
                ) : (
                  <form onSubmit={onSubmit} className="p-4 lg:p-6">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {/* Tenant code */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Tenant Code
                        </label>

                        <div className="relative">
                          <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                          <input
                            type="text"
                            value={form.tenant_code || ""}
                            disabled
                            readOnly
                            className="w-full cursor-not-allowed rounded-lg border border-slate-300 bg-slate-100 py-2 pl-9 pr-3 text-slate-700 shadow-sm"
                          />
                        </div>

                        <p className="mt-1 text-xs text-slate-500">
                          Tenant code cannot be edited.
                        </p>
                      </div>

                      {/* Username */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Username
                        </label>

                        <input
                          type="text"
                          value={username}
                          disabled
                          readOnly
                          className="w-full cursor-not-allowed rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-slate-700 shadow-sm"
                        />

                        <p className="mt-1 text-xs text-slate-500">
                          Username cannot be changed.
                        </p>
                      </div>

                      {/* Company */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Company Name
                        </label>

                        <div className="relative">
                          <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                          <input
                            name="company_name"
                            type="text"
                            value={form.company_name}
                            onChange={onChange}
                            maxLength={200}
                            placeholder="Company Pvt Ltd"
                            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Contact */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Contact Name
                        </label>

                        <input
                          name="contact_name"
                          type="text"
                          value={form.contact_name}
                          onChange={onChange}
                          maxLength={200}
                          placeholder="Contact person"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Email
                        </label>

                        <div className="relative">
                          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                          <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={onChange}
                            maxLength={256}
                            placeholder="admin@example.com"
                            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Phone
                        </label>

                        <div className="relative">
                          <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                          <input
                            name="phone"
                            type="text"
                            value={form.phone}
                            onChange={onChange}
                            maxLength={50}
                            placeholder="+91 90000 00000"
                            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Address */}
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Address
                        </label>

                        <div className="relative">
                          <MapPin className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />

                          <input
                            name="address"
                            type="text"
                            value={form.address}
                            onChange={onChange}
                            maxLength={400}
                            placeholder="Company address"
                            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Users */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Users
                        </label>

                        <div className="relative">
                          <UsersIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                          <input
                            name="num_users"
                            type="number"
                            min={1}
                            value={form.num_users}
                            onChange={onChangeUsers}
                            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Licenses */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Sentinel License Limit
                        </label>

                        <div className="relative">
                          <Boxes className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                          <input
                            name="num_licenses"
                            type="number"
                            min={0}
                            value={form.num_licenses}
                            onChange={onChangeLicenses}
                            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Clients */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Visit Client Limit
                        </label>

                        <div className="relative">
                          <UsersIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                          <input
                            name="num_clients"
                            type="number"
                            min={0}
                            value={form.num_clients}
                            onChange={onChangeClients}
                            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Map */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Map Provider
                        </label>

                        <select
                          name="user_map"
                          value={form.user_map}
                          onChange={onChange}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                        >
                          <option value="google">Google Maps</option>
                          <option value="mapbox">Mapbox</option>
                          <option value="none">No Map / Basic</option>
                        </select>
                      </div>

                      {/* Payroll */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Payroll Enabled
                        </label>

                        <select
                          name="payroll_enabled"
                          value={form.payroll_enabled ? "1" : "0"}
                          onChange={onChangePayrollEnabled}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                        >
                          <option value="0">No</option>
                          <option value="1">Yes</option>
                        </select>
                      </div>

                      {/* Notification */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Notification Source
                        </label>

                        <div className="relative">
                          <BellRing className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                          <select
                            name="notification_source"
                            value={form.notification_source}
                            onChange={onChange}
                            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          >
                            <option value="whatsapp">WhatsApp</option>
                            <option value="mail">Mail</option>
                            <option value="both">WhatsApp and Mail</option>
                          </select>
                        </div>
                      </div>

                      {/* Panic settings */}
                      <div className="md:col-span-2">
                        <div className="overflow-hidden rounded-xl border border-rose-200 bg-rose-50/40">
                          <div className="flex flex-col gap-3 border-b border-rose-200 bg-gradient-to-r from-rose-50 to-orange-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-2">
                              <Siren className="h-5 w-5 text-rose-600" />

                              <div>
                                <h4 className="text-sm font-semibold text-slate-800">
                                  Panic Call Settings
                                </h4>

                                <p className="text-xs text-slate-500">
                                  Configure the tenant-specific Plivo caller, recipients, and voice message.
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${panicStatusPill}`}
                              >
                                {panicEnabled ? "Enabled" : "Disabled"}
                              </span>

                              <button
                                type="button"
                                onClick={onTogglePanicEnabled}
                                className={`rounded-lg px-3 py-1.5 text-xs font-medium text-white ${
                                  panicEnabled
                                    ? "bg-rose-600 hover:bg-rose-700"
                                    : "bg-emerald-600 hover:bg-emerald-700"
                                }`}
                              >
                                {panicEnabled ? "Disable" : "Enable"}
                              </button>
                            </div>
                          </div>

                          <div className="space-y-5 p-4">
                            <div>
                              <label className="mb-1 block text-sm font-medium text-slate-700">
                                Plivo From Number
                              </label>

                              <div className="relative">
                                <PhoneCall className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-rose-500" />

                                <input
                                  type="tel"
                                  value={panicFromNumber}
                                  onChange={onChangePanicFromNumber}
                                  maxLength={30}
                                  placeholder="+14155552671"
                                  className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
                                />
                              </div>

                              <p className="mt-1 text-xs text-slate-500">
                                Enter the Plivo number assigned to this tenant in E.164 format.
                              </p>
                            </div>

                            <div>
                              <div className="mb-2 flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-slate-700">
                                  Panic Recipient Numbers
                                </label>

                                <button
                                  type="button"
                                  onClick={addPanicRecipient}
                                  disabled={
                                    panicRecipients.length >= MAX_PANIC_RECIPIENTS
                                  }
                                  className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                  Add Number
                                </button>
                              </div>

                              <div className="space-y-3">
                                {panicRecipients.map((recipient, index) => (
                                  <div
                                    key={`panic-recipient-${index}`}
                                    className="flex items-center gap-2"
                                  >
                                    <div className="relative flex-1">
                                      <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                                      <input
                                        type="tel"
                                        value={recipient}
                                        onChange={(event) =>
                                          onChangePanicRecipient(
                                            index,
                                            event.target.value
                                          )
                                        }
                                        maxLength={30}
                                        placeholder={`Recipient ${index + 1}, e.g. +919876543210`}
                                        className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
                                      />
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() => removePanicRecipient(index)}
                                      className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                                      title="Remove recipient"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>

                              <p className="mt-2 text-xs text-slate-500">
                                Calls will follow the displayed order. Maximum{" "}
                                {MAX_PANIC_RECIPIENTS} numbers.
                              </p>
                            </div>

                            <div>
                              <label className="mb-1 block text-sm font-medium text-slate-700">
                                Panic Call Voice Message
                              </label>

                              <div className="relative">
                                <MessageSquareText className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-rose-500" />

                                <textarea
                                  value={panicVoiceMessageTemplate}
                                  onChange={onChangePanicVoiceMessageTemplate}
                                  rows={3}
                                  maxLength={500}
                                  placeholder={DEFAULT_PANIC_VOICE_TEMPLATE}
                                  className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-rose-400 focus:ring-2 focus:ring-rose-200"
                                />
                              </div>

                              <p className="mt-1 text-xs text-slate-500">
                                This is exactly what's spoken on the panic call. Use{" "}
                                <code>{"{id}"}</code> for the guard ID and{" "}
                                <code>{"{name}"}</code> for the guard name — both are filled in automatically.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Logo */}
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Company Logo
                        </label>

                        <div className="flex flex-col items-start gap-4 sm:flex-row">
                          <div className="flex h-32 w-32 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                            {logoPreview ? (
                              <img
                                src={logoPreview}
                                alt="Company logo"
                                className="max-h-full max-w-full object-contain"
                              />
                            ) : (
                              <ImageIcon className="h-8 w-8 text-slate-300" />
                            )}
                          </div>

                          <div className="flex-1">
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm hover:bg-slate-50">
                              <ImageIcon className="h-4 w-4" />
                              <span>Choose file</span>

                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp"
                                onChange={onLogoFileChange}
                                className="hidden"
                              />
                            </label>

                            {logoFile ? (
                              <p className="mt-2 text-xs text-emerald-700">
                                Selected: {logoFile.name}
                              </p>
                            ) : null}

                            {form.logo_path ? (
                              <p className="mt-1 break-all text-xs text-slate-500">
                                Current: <code>{toAbsMediaUrl(form.logo_path)}</code>
                              </p>
                            ) : null}

                            <p className="mt-1 text-xs text-slate-500">
                              PNG, JPG or WEBP up to 2MB.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Status
                        </label>

                        <div className="flex items-center gap-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusPill}`}
                          >
                            {form.is_active ? "Active" : "Inactive"}
                          </span>

                          <button
                            type="button"
                            onClick={onToggleActive}
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-white shadow ${
                              form.is_active
                                ? "bg-rose-600 hover:bg-rose-700"
                                : "bg-emerald-600 hover:bg-emerald-700"
                            }`}
                          >
                            {form.is_active ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => navigate("/superAdmin/admin_Menu")}
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
                      >
                        Cancel
                      </button>

                      <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-white shadow hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Save className="h-4 w-4" />
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Right panel */}
              <div className="col-span-12 rounded-2xl border border-slate-200/70 bg-white/70 p-5 shadow-xl backdrop-blur xl:col-span-4">
                <h4 className="mb-3 text-sm font-semibold text-slate-800">Tips</h4>

                <ul className="space-y-2 text-sm text-slate-700">
                  <li>• Tenant code and username cannot be changed.</li>
                  <li>
                    • Plivo numbers must include the country code and start with{" "}
                    <code>+</code>.
                  </li>
                  <li>
                    • Enable panic calling only after adding the From Number and at least one recipient.
                  </li>
                  <li>• Panic recipients are called according to their displayed order.</li>
                  <li>
                    • Use <code>{"{id}"}</code> and <code>{"{name}"}</code> in the voice message — they're filled in automatically per alert.
                  </li>
                  <li>• Disabling panic calling preserves the saved numbers and message.</li>
                </ul>

                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-xs text-slate-500">Tenant ID</div>

                    <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <Building2 className="h-4 w-4 text-slate-500" />
                      {tenantId}
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-xs text-slate-500">Tenant Code</div>

                    <div className="break-all text-sm font-medium text-slate-800">
                      {form.tenant_code || "—"}
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-xs text-slate-500">User</div>

                    <div className="break-all text-sm font-medium text-slate-800">
                      {username}
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-xs text-slate-500">Panic Calls</div>

                    <div className="text-sm font-medium text-slate-800">
                      {panicEnabled
                        ? `Enabled · ${
                            panicRecipients.filter((number) =>
                              String(number || "").trim()
                            ).length
                          } recipient(s)`
                        : "Disabled"}
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-xs text-slate-500">Status</div>

                    <div className="text-sm font-medium text-slate-800">
                      {form.is_active ? "Active" : "Inactive"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}